"use client";

import { useEffect, useRef, useState, type CSSProperties } from "react";
import { Check } from "lucide-react";
import styles from "./AgentMotion.module.css";

/**
 * The looping objects on the site's three-card sections: a report that flips to
 * show the line it read, sheets dropping onto a pile, a shutter opening on a
 * tick, two halves closing a lock, sheets fanning out, a chain lighting link by
 * link, a lens moving down a list, records passing between two systems, and
 * steps rising. CSS only, so they stay sharp and cost no download.
 *
 * Animation is switched on here rather than in the stylesheet: with no
 * JavaScript, or for readers who prefer reduced motion, each object simply
 * rests in its finished pose. It also pauses while off screen.
 */
export type AgentMotionKind = "read" | "check" | "sign" | "lock" | "fan" | "chain" | "scan" | "shuttle" | "steps";

export function AgentMotion({ kind }: { kind: AgentMotionKind }) {
  const ref = useRef<HTMLDivElement>(null);
  const [run, setRun] = useState<boolean | undefined>(undefined);

  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(([e]) => setRun(e.isIntersecting));
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <div ref={ref} className={styles.stage} data-run={run} aria-hidden="true">
      {kind === "read" && <Report />}
      {kind === "check" && <Pile />}
      {kind === "sign" && <Shutter />}
      {kind === "lock" && <Lock />}
      {kind === "fan" && <Fan />}
      {kind === "chain" && <Chain />}
      {kind === "scan" && <Scan />}
      {kind === "shuttle" && <Shuttle />}
      {kind === "steps" && <Steps />}
    </div>
  );
}

function Report() {
  return (
    <div className={styles.flip}>
      <div className={`${styles.face} ${styles.front}`}>
        <i style={{ width: "62%" }} />
        <i style={{ width: "86%" }} />
        <i style={{ width: "74%" }} />
        <i style={{ width: "48%" }} />
      </div>
      <div className={`${styles.face} ${styles.back}`}>
        <i style={{ width: "52%" }} />
        <b>
          <Check className="h-3 w-3" strokeWidth={3.5} />
        </b>
        <i style={{ width: "70%" }} />
        <i style={{ width: "40%" }} />
      </div>
    </div>
  );
}

function Pile() {
  return (
    <div className={styles.pile}>
      {[0, 1, 2, 3, 4].map((i) => (
        <span key={i} className={styles.sheet} data-i={i} style={{ "--rest": `${-i * 11}px` } as CSSProperties} />
      ))}
    </div>
  );
}

function Shutter() {
  return (
    <div className={styles.iris}>
      <div className={styles.spin}>
        {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
          <span key={i} className={styles.petal} style={{ "--i": i } as CSSProperties} />
        ))}
      </div>
      <span className={styles.tick}>
        <Check className="h-7 w-7" strokeWidth={3} />
      </span>
    </div>
  );
}

function Lock() {
  return (
    <div className={styles.lock}>
      <span className={`${styles.half} ${styles.left}`} />
      <span className={`${styles.half} ${styles.right}`} />
      <span className={`${styles.tick} ${styles.lockTick}`}>
        <Check className="h-6 w-6" strokeWidth={3} />
      </span>
    </div>
  );
}

function Fan() {
  return (
    <div className={styles.fan}>
      {[0, 1, 2].map((i) => (
        <span key={i} className={styles.leaf} data-i={i}>
          <i />
          <i />
          <i />
        </span>
      ))}
    </div>
  );
}

function Chain() {
  return (
    <div className={styles.chain}>
      {[0, 1, 2, 3].map((i) => (
        <span key={i} className={styles.link} data-i={i} />
      ))}
    </div>
  );
}

function Scan() {
  return (
    <div className={styles.scan}>
      {[0, 1, 2, 3].map((i) => (
        <span key={i} className={styles.row} data-i={i} />
      ))}
      <span className={styles.lens} />
    </div>
  );
}

function Shuttle() {
  return (
    <div className={styles.shuttle}>
      <span className={styles.box} />
      <span className={styles.lane}>
        {[0, 1, 2].map((i) => (
          <i key={i} data-i={i} />
        ))}
      </span>
      <span className={`${styles.box} ${styles.boxDark}`} />
    </div>
  );
}

function Steps() {
  return (
    <div className={styles.steps}>
      {[0, 1, 2].map((i) => (
        <span key={i} className={styles.bar} data-i={i}>
          <b>
            <Check className="h-4 w-4" strokeWidth={3.5} />
          </b>
        </span>
      ))}
    </div>
  );
}
